/**
 * @module support/shared/widgets
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */

import { NgModule, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA } from '@angular/core';
import { CommonModule, CurrencyPipe, DecimalPipe, TitleCasePipe } from '@angular/common';
import { CalendarModule } from 'primeng/calendar';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { DropdownModule } from 'primeng/dropdown';
import { DialogModule } from 'primeng/dialog';
import { AccordionModule } from 'primeng/accordion';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonComponent } from '@support/shared/widgets/components/button/button.component';
import { CheckboxComponent } from '@support/shared/widgets/components/checkbox/checkbox.component';
import { RadioButtonComponent } from './components/radio-button/radio-button.component';
import { CalendarComponent } from '@support/shared/widgets/components/calendar/calendar.component';
import { BarCodeCamScannerComponent } from '@support/shared/widgets/components/bar-code-cam-scanner/bar-code-cam-scanner.component';
import { ButtonSizePipe } from '@support/shared/widgets/pipes/button-size.pipe';
import { ButtonTypePipe } from '@support/shared/widgets/pipes/button-type.pipe';
import { PhonePipe } from '@support/shared/widgets/pipes/phone/phone.pipe';
import { TmoMessageComponent } from '@support/shared/widgets/components/tmo-message/tmo-message.component';
import { InputComponent } from '@support/shared/widgets/components/input/input.component';
import { TextareaComponent } from '@support/shared/widgets/components/textarea/textarea.component';
import { DropdownComponent } from '@support/shared/widgets/components/dropdown/dropdown.component';
import { LegalTextComponent } from './components/legal-text/legal-text.component';
import { SpinnerComponent } from '@support/shared/widgets/components/spinner/spinner.component';
import { StarRatingComponent } from '@support/shared/widgets/components/star-rating/star-rating.component';
import { InlineSpinnerComponent } from '@support/shared/widgets/components/inline-spinner/inline-spinner.component';
import { CarouselComponent } from '@support/shared/widgets/components/carousel/carousel.component';
import { SlideComponent } from '@support/shared/widgets/components/slide/slide.component';
import { TitleHeaderSpacingDirective } from '@support/shared/widgets/directives/title-header-spacing.directive';
import { DeviceDetectionService } from '@support/shared/widgets/services/device-detection.service';
import { StaticModalComponent } from './components/static-modal/static-modal.component';
import { AddressValidatorComponent } from './components/address-validator/address-validator.component';
import { AddressFormComponent } from './components/address-form/address-form.component';
import { PageTitlesComponent } from './components/page-titles/page-titles.component';
import { DynamicModalComponent } from './components/dynamic-modal/dynamic-modal.component';
import { SeeMoreSeeLessComponent } from './components/see-more-see-less/see-more-see-less.component';
import { DateDirective } from '@support/shared/widgets/directives/date.directive';
import { CurrencyDirective } from '@support/shared/widgets/directives/currency.directive';
import { GoTopButtonDirective } from '@support/shared/widgets/directives/go-top-button.directive';
import { ScrollToTopDirective } from '@support/shared/widgets/directives/scroll-top-repdash.directive';
import { TmoCarouselModule } from '@support/shared/carousel/carousel.module';
import { ReportModalComponent } from '@support/shared/widgets/components/report-modal/report-modal.component';
import { EPinConflictModalComponent } from '@support/shared/widgets/components/epin-conflict-modal/epin-conflict-modal.component';
import { CustomerAuthenticationModalComponent } from '@support/shared/widgets/components/customer-authentication-modal/customer-authentication-modal.component';
import { TouristPlanConflictModalComponent } from '@support/shared/widgets/components/tourist-plan-conflict-modal/tourist-plan-conflict-modal.component';
import { TruncatePipe } from '@support/shared/widgets/pipes/truncate/truncate.pipe';
import { AccordionComponent } from './components/accordion/accordion.component';
import { TmoAccordionComponent } from './components/tmo-accordion/tmo-accordion.component';
import { TooltipComponent } from './components/tooltip/tooltip.component';
import { InputAutocompleteComponent } from './components/input-autocomplete/input-autocomplete.component';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { KeyValuePipe } from './pipes/key-value/key-value.pipe';
import { OverrideModalComponent } from './components/override-modal/override-modal.component';
import { OverridePriceModalComponent } from './components/override-price-modal/override-price-modal.component';
import { AllowedInputDirective } from '@support/shared/widgets/directives/allowed-input.directive';
import { MarketingCarouselComponent } from '@support/shared/widgets/components/marketing-carousel/marketing-carousel.component';
import { DollarPipe } from './pipes/dollar/dollar.pipe';
import { SafeHtmlPipe } from './pipes/safe/safe-html.pipe';
import { DigitsPipe } from './pipes/digits/digits.pipe';
import { LoopUnrollPipe } from './pipes/loop-unroll/loop-unroll.pipe';
import { ClickOutsideDirective } from '@support/shared/widgets/directives/click-outside.directive';
import { SetFocusDirective } from '@support/shared/widgets/directives/set-focus.directive';
import { UIPermissionDirective } from './directives/ui-permission.directive';
import { AutoFocusDirective } from './directives/autofocus.directive';
import { ScrollListenerDirective } from '@support/shared/widgets/directives/scroll-listener.directive';
import { FormInputsDirective } from '@support/shared/widgets/directives/form-inputs.directive';
import { CalendarMenuDirective } from '@support/shared/widgets/directives/calendar-menu.directive';
import { RemoveRoleTabDirective } from '@support/shared/widgets/directives/remove-role-tab.directive';
import { TogglePlaceHolderDirective } from '@support/shared/widgets/directives/toggle-placeholder.directive';
import { SignaturePadDirective } from './directives/signature-pad.directive';
import { FraudReviewModalComponent } from '@support/shared/widgets/components/fraud-review-modal/fraud-review-modal.component';
import { FraudRejectedModalComponent } from '@support/shared/widgets/components/fraud-rejected-modal/fraud-rejected-modal.component';
import { ErrorMessageDetailComponent } from './components/error-message-detail/error-message-detail.component';
import { AddressFormService } from '@support/shared/widgets/components/address-form/service/address-form.service';
import { PopupComponent } from '@support/shared/widgets/components/popup/popup-component';
import { TaxExemptionModalComponent } from '@support/shared/widgets/components/tax-exemption-modal/tax-exemption-modal.component';
import { OneTimePaymentService } from '@experience/one-time-payment/services/one-time-payment.service';
import { PaymentCardsService } from '@support/payment-cards/services/payment-cards.service';
import { LoadAuthenticationGuardService } from '@support/shared/widgets/services/load-authentication-guard.service';
import { PermissionOverrideModalComponent } from '@support/shared/widgets/components/permission-override-modal/permission-override-modal.component';
import { AddressErrorMessageComponent } from '@support/shared/widgets/components/address-error-message/address-error-message.component';
import { MultiLineMsisdnDropDownComponent } from '@support/shared/widgets/components/multiline-msisdn-dropdown/multiline-msisdn-dropdown.component';
import { CartNotEmptyModalComponent } from '@support/shared/widgets/components/cart-not-empty-modal/cart-not-empty-modal.component';
import { DocumentErrorComponent } from '@support/shared/widgets/components/document-error/document-error.component';
import { DropDownDirective } from '@support/shared/widgets/directives/dropdown.directive';
import { TmoAutoLoginDirective } from '@support/shared/widgets/directives/tmo-auto-login/tmo-auto-login.directive';
import { GlobalErrorMessageComponent } from '@support/shared/widgets/components/global-error-message/global-error-message.component';
import { TmoSecureTransactionComponent } from '@support/shared/widgets/components/secure-transaction/secure-transaction.component';
import { CanDeactivateGuard } from './services/deactive-guard.service';
import { PaymentDisclaimerModalComponent } from '@support/payment-disclaimer-modal/components/payment-disclaimer-modal.component';
import { CaptchaComponent } from './components/re-captcha/re-captcha.component';
import { RecaptchaModule } from 'ng-recaptcha-2';
import { MultiStepsThreadComponent } from '@support/shared/widgets/components/multi-steps-thread/multi-steps-thread-component';
import { InvisibleCaptchaComponent } from '@support/shared/widgets/components/invisible-re-captcha/invisible-re-captcha.component';
import { LoadGuardService } from './services/load-guard.service';
import { CancelCartButtonComponent } from './components/cancel-cart-button/cancel-cart-button.component';
import { AddressFormValidatorComponent } from '@support/shared/widgets/components/address-form-validator/address-form-validator.component';
import { OneTimePinValidationComponent } from '@experience/one-time-pin/components/one-time-pin.component';
import { OneTimePinValidationService } from '@experience/one-time-pin/services/one-time-pin.service';
import { FormPdlDirective } from '@support/shared/widgets/directives/form-pdl.directive';
import { FormPdlComponent } from '@support/shared/widgets/components/form-pdl/form-pdl.component';
import { PdlErrorDirective } from '@support/shared/widgets/directives/pdl-error';
import { DashedInputDirective } from '@support/shared/widgets/directives/dashed-input.directive';
import { OneTimePinInputComponent } from '@support/shared/widgets/components/otp-input/one-time-pin-input.component';
import { SimValidationComponent } from '@support/shared/widgets/components/sim-validation/sim-validation.component';
import { DeviceValidationComponent } from '@support/shared/widgets/components/device-validation/device-validation.component';
import { FloatingInputComponent } from './components/floatingInputBox/floatingInput.component';
import { DigitalDynamicModalComponent } from './components/digital-dynamic-modal/digital-dynamic-modal.component';
import { DigitalCustomerAuthenticationModalComponent } from './components/digital-customer-authentication-modal/digital-customer-authentication-modal.component';
import { JunkFeeBannerComponent } from './components/junk-fee-banner/junk-fee-banner.component';
@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    DropdownModule,
    CalendarModule,
    DialogModule,
    AccordionModule,
    ReactiveFormsModule,
    AutoCompleteModule,
    TooltipModule,
    ReactiveFormsModule,
    TmoCarouselModule,
    RecaptchaModule,
  ],
  declarations: [
    ButtonComponent,
    CalendarComponent,
    CheckboxComponent,
    FraudRejectedModalComponent,
    RadioButtonComponent,
    ButtonSizePipe,
    ButtonTypePipe,
    FraudReviewModalComponent,
    InputComponent,
    DropdownComponent,
    LegalTextComponent,
    SpinnerComponent,
    StarRatingComponent,
    TmoMessageComponent,
    PhonePipe,
    TitleHeaderSpacingDirective,
    StaticModalComponent,
    AddressValidatorComponent,
    CancelCartButtonComponent,
    AddressFormValidatorComponent,
    AddressFormComponent,
    TextareaComponent,
    PageTitlesComponent,
    DynamicModalComponent,
    DateDirective,
    SeeMoreSeeLessComponent,
    CurrencyDirective,
    ReportModalComponent,
    TmoSecureTransactionComponent,
    EPinConflictModalComponent,
    BarCodeCamScannerComponent,
    TouristPlanConflictModalComponent,
    TmoAccordionComponent,
    TooltipComponent,
    TruncatePipe,
    AccordionComponent,
    InputAutocompleteComponent,
    KeyValuePipe,
    AddressErrorMessageComponent,
    SimValidationComponent,
    GoTopButtonDirective,
    ScrollToTopDirective,
    OverrideModalComponent,
    DeviceValidationComponent,
    OverridePriceModalComponent,
    CarouselComponent,
    SlideComponent,
    PopupComponent,
    MarketingCarouselComponent,
    RemoveRoleTabDirective,
    CalendarMenuDirective,
    AllowedInputDirective,
    DollarPipe,
    SafeHtmlPipe,
    DigitsPipe,
    ClickOutsideDirective,
    SetFocusDirective,
    UIPermissionDirective,
    AutoFocusDirective,
    InlineSpinnerComponent,
    ScrollListenerDirective,
    LoopUnrollPipe,
    FormInputsDirective,
    SignaturePadDirective,
    TogglePlaceHolderDirective,
    ErrorMessageDetailComponent,
    TaxExemptionModalComponent,
    PermissionOverrideModalComponent,
    GlobalErrorMessageComponent,
    CustomerAuthenticationModalComponent,
    MultiLineMsisdnDropDownComponent,
    CartNotEmptyModalComponent,
    DocumentErrorComponent,
    DropDownDirective,
    TmoAutoLoginDirective,
    PaymentDisclaimerModalComponent,
    CaptchaComponent,
    InvisibleCaptchaComponent,
    MultiStepsThreadComponent,
    OneTimePinValidationComponent,
    FormPdlDirective,
    FormPdlComponent,
    PdlErrorDirective,
    DashedInputDirective,
    OneTimePinInputComponent,
    FloatingInputComponent,
    DigitalDynamicModalComponent,
    DigitalCustomerAuthenticationModalComponent,
    JunkFeeBannerComponent,
  ],

  exports: [
    ButtonComponent,
    CalendarComponent,
    CheckboxComponent,
    RemoveRoleTabDirective,
    CalendarMenuDirective,
    RadioButtonComponent,
    ButtonSizePipe,
    ButtonTypePipe,
    FraudRejectedModalComponent,
    BarCodeCamScannerComponent,
    InputComponent,
    DropdownComponent,
    LegalTextComponent,
    SpinnerComponent,
    StarRatingComponent,
    TmoMessageComponent,
    PhonePipe,
    TitleHeaderSpacingDirective,
    DialogModule,
    StaticModalComponent,
    FraudReviewModalComponent,
    ReportModalComponent,
    TmoSecureTransactionComponent,
    EPinConflictModalComponent,
    TouristPlanConflictModalComponent,
    TruncatePipe,
    TitleHeaderSpacingDirective,
    CancelCartButtonComponent,
    DialogModule,
    StaticModalComponent,
    AddressFormValidatorComponent,
    AddressFormComponent,
    AddressValidatorComponent,
    TextareaComponent,
    PageTitlesComponent,
    AddressErrorMessageComponent,
    SimValidationComponent,
    DeviceValidationComponent,
    DynamicModalComponent,
    DateDirective,
    CurrencyDirective,
    SeeMoreSeeLessComponent,
    AccordionModule,
    TmoAccordionComponent,
    AccordionComponent,
    TooltipComponent,
    InputAutocompleteComponent,
    KeyValuePipe,
    GoTopButtonDirective,
    ScrollToTopDirective,
    AllowedInputDirective,
    CarouselComponent,
    SlideComponent,
    MarketingCarouselComponent,
    PopupComponent,
    CustomerAuthenticationModalComponent,
    OverrideModalComponent,
    OverridePriceModalComponent,
    CaptchaComponent,
    MultiStepsThreadComponent,
    DollarPipe,
    SafeHtmlPipe,
    DigitsPipe,
    ClickOutsideDirective,
    SetFocusDirective,
    UIPermissionDirective,
    AutoFocusDirective,
    InlineSpinnerComponent,
    ScrollListenerDirective,
    LoopUnrollPipe,
    FormInputsDirective,
    SignaturePadDirective,
    TogglePlaceHolderDirective,
    ErrorMessageDetailComponent,
    TaxExemptionModalComponent,
    PermissionOverrideModalComponent,
    GlobalErrorMessageComponent,
    MultiLineMsisdnDropDownComponent,
    CartNotEmptyModalComponent,
    DocumentErrorComponent,
    DropDownDirective,
    TmoAutoLoginDirective,
    PaymentDisclaimerModalComponent,
    OneTimePinValidationComponent,
    FormPdlDirective,
    FormPdlComponent,
    PdlErrorDirective,
    OneTimePinInputComponent,
    FloatingInputComponent,
    DigitalDynamicModalComponent,
    DigitalCustomerAuthenticationModalComponent,
    JunkFeeBannerComponent,
  ],

  providers: [
    DecimalPipe,
    CurrencyPipe,
    DeviceDetectionService,
    AddressFormService,
    TitleCasePipe,
    OneTimePaymentService,
    PaymentCardsService,
    CanDeactivateGuard,
    LoadGuardService,
    LoadAuthenticationGuardService,
    OneTimePinValidationService,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
})
export class WidgetsModule {}
